import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMount } from "react-use";
import _ from "lodash";
import { useSnackbar } from "notistack";

import analytics from "fond/analytics";
import { uploadAttachmentAbort, uploadAttachments } from "fond/redux/attachments";
import { AppThunkDispatch, Store } from "fond/types";

import AttachmentUploadListItem from "./AttachmentUploadListItem";
import AttachmentUploadTotalProgress from "./AttachmentUploadTotalProgress";

import { AttachmentUploadContainer, AttachmentUploadList, AttachmentUploadProgress } from "./AttachmentUpload.styles";

type AttachmentUploadProps = {
  /*
   * The list of files requiring upload.
   */
  files: File[];
  /**
   * Callback function that returns true / false whenever upload move between being active (true) and failed, cancelled or completed (false).
   * Note that uploads can be retried on failure or cancellation. An upload flow may move into progress many times.
   */
  onIsUploading(isUploading: boolean): void;
  /**
   * The unique identifier for a feature belonging to a layer on the map.
   */
  featureId?: string | null;
  /**
   * The version to associate with all uploaded files, if selected.
   */
  versionId?: string | null;
};

const AttachmentUpload: React.FC<AttachmentUploadProps> = ({ files, onIsUploading, featureId, versionId }: AttachmentUploadProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch: AppThunkDispatch = useDispatch();

  const { uploadStatus } = useSelector((state: Store) => state.attachments);
  const { projectId } = useSelector((state: Store) => state.project);

  /*
   * Start the upload automatically.
   */
  useMount(() => {
    startUpload(files);
  });

  /*
   * Upload controls.
   */
  const startUpload = (targetFiles: File[]) => {
    onIsUploading(true);
    dispatch(uploadAttachments(projectId, targetFiles, featureId, undefined, versionId))
      .catch(() => enqueueSnackbar("Some attachments failed to upload."))
      .finally(() => onIsUploading(false));
  };
  const abortUpload = (file: File) => {
    dispatch(uploadAttachmentAbort(file));
    analytics.track("attachments.upload.item.cancel.clicked");
  };
  const retryUpload = (file: File) => {
    startUpload([file]);
    analytics.track("attachments.upload.item.retry.clicked");
  };

  return (
    <AttachmentUploadContainer className="customScrollbars">
      <AttachmentUploadProgress>
        <AttachmentUploadTotalProgress data-testid="attachment-upload-total-progress" files={files} fileUploadStatus={uploadStatus} />
      </AttachmentUploadProgress>
      <AttachmentUploadList>
        {_.sortBy(files, "size").map((file: File) => (
          <AttachmentUploadListItem
            data-testid="attachment-upload-list-item"
            key={file.name}
            file={file}
            progress={uploadStatus[file.name]?.progress || 0}
            isError={uploadStatus[file.name]?.isError || uploadStatus[file.name]?.isAborted}
            error={uploadStatus[file.name]?.error}
            onAbort={abortUpload}
            onRetry={retryUpload}
          />
        ))}
      </AttachmentUploadList>
    </AttachmentUploadContainer>
  );
};

export default AttachmentUpload;
